import { zodResolver } from '@hookform/resolvers/zod';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';

import type { BillingInfoFormData, BillingInfoFormHandlers } from '@ctct/application-layer';
import { type BillingStrategyQuery } from '@ctct/domain-layer';
import { Button } from '@ctct/payflow-ui';
import { Col, Container, FormWrapper, Row } from '@ctct/presentation-layer';
import type { BillingInfoFormHooks } from '@ctct/state-client-layer';

import { BillingInfoCard } from './components/BillingInfoCard';
import { FormWithinContext } from './FormWithinContext';

import './components/styles.scss';

interface Props {
  data: BillingInfoFormData;
  handlers: BillingInfoFormHandlers;
  hooks: BillingInfoFormHooks;
  billingStrategy: BillingStrategyQuery;
}

export const BillingInfoFormWidgetContent = ({ data, handlers, hooks, billingStrategy }: Props) => {
  const { t } = useTranslation();

  const isLoading = hooks.useIsLoading();
  const isDisplayingCard = hooks.useIsDisplayingCard();
  const canDisplayCard = hooks.useCanDisplayCard();

  if (isDisplayingCard) {
    return <BillingInfoCard data={data} handlers={handlers} hooks={hooks} />;
  }

  return (
    <>
      <FormWrapper
        onSubmit={noop}
        loading={isLoading}
        values={data.getDefaultValues()}
        resolver={zodResolver(data.getZodSchema())}
      >
        <FormWithinContext data={data} handlers={handlers} billingStrategy={billingStrategy} />
      </FormWrapper>

      {canDisplayCard && (
        <Container>
          <Row>
            <Col />
            <Col fixed>
              <Button style={{ marginTop: '16px' }} variant="text" onClick={handlers.onCancelClick}>
                {t('billing-info-form.cancel')}
              </Button>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};
